import React, { useCallback } from 'react';
import { useDroppable } from '@dnd-kit/core';
import BattlefieldCard from './BattlefieldCard';
import { DROP_IDS } from '../hooks/usePlaytestDnd';
import { CARD_SIZE, getAreas, getCardRotation, getCardSizePx, getDividers } from '../utils/boardGeometry';
import { getPlayerColor } from '../utils/playerUtils';
import { PING_VISIBLE_MS } from '../../../context/PlaytestContext';
import './Battlefield.css';

/** Rasterweite in Weltkoordinaten: ein Viertel Kartenbreite. */
const GRID_WORLD = CARD_SIZE.w / 4;

const sameUser = (a, b) => a != null && b != null && String(a) === String(b);

/** Achsenparalleles Bildschirm-Rechteck aus zwei Weltpunkten (bei 180° tauschen die Ecken). */
function screenBox(toScreen, a, b) {
  const p = toScreen(a);
  const q = toScreen(b);
  return {
    left: Math.min(p.x, q.x),
    top: Math.min(p.y, q.y),
    width: Math.abs(q.x - p.x),
    height: Math.abs(q.y - p.y),
  };
}

/** Position des Teilnehmers mit dem jüngsten noch sichtbaren Ping je Karte. */
function pingedPositions(pings, players, now) {
  const byCard = new Map();
  for (const ping of pings) {
    if (now - ping.at >= PING_VISIBLE_MS) continue;
    const latest = byCard.get(ping.instanceId);
    if (latest && latest.at > ping.at) continue;
    byCard.set(ping.instanceId, ping);
  }
  const positions = new Map();
  for (const [instanceId, ping] of byCard) {
    const position = players.find((p) => sameUser(p.userId, ping.userId))?.position;
    if (position !== undefined) positions.set(instanceId, position);
  }
  return positions;
}

/**
 * Brettfläche: Flächen der Teilnehmer, Trennlinien, alle Brettkarten, Drop-Ziel.
 *
 *   const camera = useCamera({ viewportRef, playerCount, viewerPosition });
 *   <Battlefield players={state.players} pings={state.pings} viewerUserId={me} playerCount={n} camera={camera}
 *     onBackgroundContextMenu={(worldPt, event) => …}
 *     onCardTap={…} onCardPing={…} onCardBringToFront={…} onCardOpenMenu={…}
 *     previewFlipFor={…} onCardCounterAdd={…} onCardCounterRemove={…} onCardHoverPreview={bind} />
 *
 * - `camera` ist das Ergebnis von `useCamera`; die Brettfläche hängt dessen `bindViewport` und
 *   `handlers` an ihr Element und rechnet alle Lagen über `camera.toScreen` (boardGeometry).
 * - Flächen aus `getAreas(playerCount)`, getönt in der Teilnehmerfarbe; gezeichnet werden nur
 *   belegte Flächen, Trennlinien nur zwischen zwei belegten (`getDividers`).
 * - Karten aller Teilnehmer: `card.position` ist die Kartenmitte in Weltkoordinaten, die Breite
 *   `getCardSizePx(zoom)`, die Reihenfolge `card.zIndex`. Die `onCard*`-Callbacks, `canAct`
 *   (false: der Betrachter ist ausgeschieden und sieht nur zu) und `isMobile` gehen unverändert
 *   an `BattlefieldCard` (Signaturen dort).
 * - `previewFlipFor(card)` liefert die Umdreh-Funktion der Großansicht, solange sie zu dieser Karte
 *   steht, sonst `undefined`; sie geht als `onViewFlip` an die Karte (nur diese trägt den Knopf).
 * - Eigener Stapelkontext (`isolation: isolate`): die zIndex-Werte der Karten wachsen mit jeder
 *   neu ausgelegten Karte und dürfen nie über Kopfzeile, Hand oder Kacheln steigen.
 * - `pings` (`state.pings`): je Karte bekommt `BattlefieldCard` als `pingedBy` die Position des
 *   Teilnehmers mit dem jüngsten Ping, der jünger als `PING_VISIBLE_MS` ist; das Entfernen
 *   abgelaufener Pings (`PING_EXPIRED`) übernimmt `usePlaytestSocket`.
 * - Textur: Raster aus `--battlefield-grid` (`GRID_WORLD` · Zoom), das mit der Kamera wandert
 *   (`background-position` am Weltursprung); `currentTurnUserId` markiert die eigene Fläche mit
 *   `data-active-turn` (goldener Rand), die eigene trägt immer `data-own`.
 * - Drop-Ziel `DROP_IDS.battlefield` (dnd-kit); `data-over` markiert einen Zug über der Fläche.
 * - Rechtsklick auf freie Fläche → `onBackgroundContextMenu(worldPt, event)`; das Ereignis wird
 *   nicht abgebrochen, ein umschließendes Radix-Kontextmenü öffnet sich also weiterhin.
 *   Rechtsklicks auf Karten erreichen die Fläche nicht.
 */
export default function Battlefield({
  players = [],
  pings = [],
  viewerUserId,
  currentTurnUserId,
  playerCount,
  camera,
  onBackgroundContextMenu,
  onCardTap,
  onCardPing,
  onCardBringToFront,
  onCardOpenMenu,
  previewFlipFor,
  onCardCounterAdd,
  onCardCounterRemove,
  onCardHoverPreview,
  canAct = true,
  isMobile = false,
}) {
  const { isOver, setNodeRef } = useDroppable({ id: DROP_IDS.battlefield });
  const { bindViewport, handlers, toScreen, toWorld, camera: cam } = camera;

  const ref = useCallback((node) => {
    setNodeRef(node);
    bindViewport(node);
  }, [setNodeRef, bindViewport]);

  const viewerPosition = players.find((p) => sameUser(p.userId, viewerUserId))?.position ?? 0;
  const occupied = players.map((p) => p.position);

  const handleContextMenu = (e) => {
    if (!onBackgroundContextMenu) return;
    const world = toWorld({ x: e.clientX, y: e.clientY });
    if (world) onBackgroundContextMenu(world, e);
  };

  let content = null;
  let gridStyle;
  if (cam) {
    const origin = toScreen({ x: 0, y: 0 });
    gridStyle = {
      '--battlefield-grid': `${GRID_WORLD * cam.zoom}px`,
      backgroundPosition: `${origin.x}px ${origin.y}px`,
    };
    const sizePx = getCardSizePx(cam.zoom);
    const heightPx = sizePx * 1.4;
    const pinged = pingedPositions(pings, players, Date.now());
    content = (
      <>
        <div className="battlefield__areas" aria-hidden>
          {getAreas(playerCount)
            .filter((area) => occupied.includes(area.position))
            .map(({ position, rect }) => (
              <div
                key={position}
                className="battlefield__area"
                data-area-position={position}
                data-own={position === viewerPosition ? 'true' : undefined}
                data-active-turn={
                  position === viewerPosition && sameUser(currentTurnUserId, viewerUserId) ? 'true' : undefined
                }
                style={{
                  ...screenBox(toScreen, { x: rect.x, y: rect.y }, { x: rect.x + rect.w, y: rect.y + rect.h }),
                  '--battlefield-area-color': getPlayerColor(position),
                }}
              />
            ))}
          {getDividers(playerCount, occupied).map(({ a, b }) => {
            const box = screenBox(toScreen, a, b);
            const vertical = box.width === 0;
            return (
              <div
                key={`${a.x},${a.y},${b.x},${b.y}`}
                className={`battlefield__divider battlefield__divider--${vertical ? 'v' : 'h'}`}
                style={box}
              />
            );
          })}
        </div>
        {players.flatMap((player) => (player.battlefield || []).map((card) => {
          const center = toScreen(card.position || { x: 0, y: 0 });
          return (
            <div
              key={card.instanceId}
              className="battlefield__slot"
              style={{
                transform: `translate(${center.x - sizePx / 2}px, ${center.y - heightPx / 2}px)`,
                zIndex: card.zIndex ?? 0,
              }}
            >
              <BattlefieldCard
                card={card}
                ownerPosition={player.position}
                viewerRotation={getCardRotation(player.position, viewerPosition, playerCount)}
                isOwn={sameUser(player.userId, viewerUserId)}
                canAct={canAct}
                isMobile={isMobile}
                sizePx={sizePx}
                pingedBy={pinged.get(card.instanceId) ?? null}
                onTap={onCardTap}
                onPing={onCardPing}
                onBringToFront={onCardBringToFront}
                onOpenMenu={onCardOpenMenu}
                onViewFlip={previewFlipFor?.(card)}
                onCounterAdd={onCardCounterAdd}
                onCounterRemove={onCardCounterRemove}
                onHoverPreview={onCardHoverPreview}
              />
            </div>
          );
        }))}
      </>
    );
  }

  return (
    <div
      ref={ref}
      className="battlefield"
      data-testid="battlefield"
      data-over={isOver ? 'true' : undefined}
      style={gridStyle}
      {...handlers}
      onContextMenu={handleContextMenu}
    >
      {content}
    </div>
  );
}
